import React from "react";
import { Users, BookOpen, IndianRupee, Wallet } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SectionHead, fmt } from "./sections";

const SAMPLE = {
  stats: [[Users, "Total students", "48"], [BookOpen, "Active enrollments", "36"], [IndianRupee, "Eligible commissions", fmt(42600)], [Wallet, "Available balance", fmt(18200)]],
  enrollments: [["Student A", "Sample course 1", "Active"], ["Student B", "Sample course 2", "Active"], ["Student C", "Sample course 1", "Active"]],
  txns: [["Course sale", "+" + fmt(3000), "Eligible"], ["Course sale", "+" + fmt(2400), "Pending"], ["Withdrawal", "−" + fmt(10000), "Paid"]],
};

export function DashboardPreview() {
  return (
    <section className="bg-muted/40" data-testid="fr-dashboard-preview">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <SectionHead eyebrow="Partner dashboard" title="See your institute at a glance" sub="A preview of the institute dashboard. All figures below are sample data for illustration." />
        <div className="relative rounded-3xl bg-white border border-border shadow-2xl overflow-hidden">
          <div className="absolute top-4 right-4 z-10 text-[11px] font-bold uppercase tracking-wider bg-amber text-white px-3 py-1 rounded-full" data-testid="fr-sample-label">Sample data</div>
          <div className="flex">
            <div className="hidden md:flex w-52 bg-[#111111] text-white/70 flex-col gap-1 p-4 text-sm">
              <div className="bg-white rounded-md p-1.5 w-fit mb-4"><img src="/viriks-logo.jpg" alt="VIRIKS" className="h-5" /></div>
              {["Dashboard", "Students", "Enroll Student", "Commissions", "Withdrawals", "Bank Account"].map((n, i) => <div key={n} className={`px-3 py-2 rounded-md ${i === 0 ? "bg-amber text-white" : ""}`}>{n}</div>)}
            </div>
            <div className="flex-1 p-5 md:p-8 space-y-6 min-w-0">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {SAMPLE.stats.map(([Icon, l, v]) => (
                  <div key={l} className="border border-border rounded-xl p-4"><div className="flex justify-between text-xs text-muted-foreground">{l}<Icon size={15} className="text-amber" /></div><div className="font-heading text-xl md:text-2xl font-black text-navy mt-2">{v}</div></div>
                ))}
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                <MiniTable title="Recent student enrollments" rows={SAMPLE.enrollments} />
                <MiniTable title="Commission & withdrawal history" rows={SAMPLE.txns} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniTable({ title, rows }) {
  return (
    <div className="border border-border rounded-xl p-4">
      <div className="font-semibold text-sm text-navy mb-3">{title}</div>
      {rows.map((r, i) => (
        <div key={i} className="flex justify-between gap-3 py-2 border-t border-border text-xs">
          <span className="font-medium text-navy truncate">{r[0]}</span><span className="text-muted-foreground truncate">{r[1]}</span><span className="text-emerald-600 font-semibold shrink-0">{r[2]}</span>
        </div>
      ))}
    </div>
  );
}

export function Faq({ fees, pct = 30 }) {
  const total = fmt(fees?.total ?? 29500);
  const qa = [
    ["What is a VIRIKS Institute Franchise?", "It lets your institute offer VIRIKS-authorized training programs, enroll students on the VIRIKS platform and earn a share of eligible commissionable revenue through a dedicated partner dashboard."],
    ["What is the franchise fee?", `The franchise fee is ${fmt(fees?.fee ?? 25000)} plus GST. The total payable (${total}) is shown before payment.`],
    ["Is GST charged separately?", `Yes. GST at ${fees?.gst_percent ?? 18}% is added to the franchise fee and shown as a separate line at checkout.`],
    ["How do I apply?", "Create or log in to your VIRIKS account, fill in the online application with your institute details and supporting documents, then pay the franchise fee."],
    ["When will my institute account be activated?", "Your partner dashboard is activated only after the franchise fee payment is confirmed and the VIRIKS team approves your application."],
    [`How does the ${pct}:${100 - pct} commission model work?`, `On eligible commissionable revenue from your students' course enrollments, ${pct}% is credited to your institute and ${100 - pct}% is retained by VIRIKS, as training is delivered by VIRIKS trainers. Commissions show as pending until confirmed by VIRIKS, then become eligible for withdrawal.`],
    ["How do I enroll students?", "Students can register and buy courses using your institute code or link, or you can enroll a student directly from your dashboard and pay for the course online."],
    ["How do I verify my bank account?", "Add your bank details in the dashboard and pay the ₹9 verification fee. The VIRIKS team then reviews the account; the fee alone does not verify ownership."],
    ["How do commission withdrawals work?", "Once your bank account is verified, you can request a withdrawal from your eligible available balance. Approved withdrawals are paid out to your verified bank account and the transfer reference is shown in your dashboard."],
    ["What happens if my application needs additional documents?", "Your application status changes to “Documents required” with a note from our team. Upload the requested documents from the application page and it returns to review."],
  ];
  return (
    <section className="max-w-4xl mx-auto px-6 py-24" data-testid="fr-faq">
      <SectionHead eyebrow="FAQs" title="Frequently asked questions" />
      <Accordion type="single" collapsible className="space-y-3">
        {qa.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} className="bg-white border border-border rounded-xl px-5" data-testid={`fr-faq-${i}`}>
            <AccordionTrigger className="text-left font-semibold text-navy">{q}</AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
